import { ReactNode, useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth-context';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { organizationService } from '@/services/api/organization.service';
import { isTherapyPractice } from '@/hooks/use-sessions';
import {
  Calendar,
  CalendarCheck,
  Settings,
  BarChart3,
  Package,
  Clock,
  Menu,
  CreditCard,
  Users,
  Lightbulb,
  UserSquare2,
  LayoutGrid,
  HelpCircle,
  Lock,
  NotebookPen,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import { Sidebar } from '@/components/chrome/Sidebar';
import { TopBar } from '@/components/chrome/TopBar';
import { TimezoneIndicator } from '@/components/chrome/TimezoneIndicator';
import { LanguagePill } from '@/components/chrome/LanguagePill';
import {
  MobileTabBar,
  type MobileTabItem,
} from '@/components/chrome/MobileTabBar';
import {
  PageHeaderProvider,
  usePageHeader,
} from '@/components/chrome/PageHeader';
import { IconButton } from '@/components/ui/icon-button';
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner';
import { OnboardingTour } from '@/components/OnboardingTour';

interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * Map the current pathname to a TopBar greeting title using the same nav
 * translation keys that label sidebar items. Returns null when no match —
 * the consumer falls back to a default greeting or the page-provided header.
 */
function useRouteGreeting(): { title: string; sub?: string } | null {
  const [location] = useLocation();
  const { t } = useTranslation('common');

  return useMemo(() => {
    const routes: Array<{ match: (p: string) => boolean; key: string }> = [
      { match: (p) => p.startsWith('/admin/dashboard') || p.startsWith('/staff/dashboard'), key: 'dashboard' },
      { match: (p) => p.startsWith('/staff/schedule'), key: 'mySchedule' },
      { match: (p) => p.startsWith('/staff/bookings'), key: 'myBookings' },
      { match: (p) => p.startsWith('/admin/availability'), key: 'schedule' },
      { match: (p) => p.startsWith('/admin/bookings'), key: 'bookings' },
      { match: (p) => p.startsWith('/admin/new-booking'), key: 'newBooking' },
      { match: (p) => p.startsWith('/admin/services'), key: 'services' },
      { match: (p) => p.startsWith('/admin/team'), key: 'team' },
      { match: (p) => p.startsWith('/admin/customers'), key: 'customers' },
      { match: (p) => p.startsWith('/admin/booking-page'), key: 'bookingPage' },
      { match: (p) => p.startsWith('/admin/sessions'), key: 'sessions' },
      { match: (p) => p.startsWith('/admin/billing'), key: 'billing' },
      { match: (p) => p.startsWith('/admin/reports'), key: 'reports' },
      { match: (p) => p.startsWith('/admin/organization'), key: 'organization' },
      { match: (p) => p.startsWith('/admin/settings') || p.startsWith('/staff/settings'), key: 'settings' },
      { match: (p) => p.startsWith('/admin/calendar-settings') || p.startsWith('/staff/calendar-settings'), key: 'calendarIntegration' },
      { match: (p) => p.startsWith('/admin/tips'), key: 'helpTips' },
      { match: (p) => p.startsWith('/admin/onboarding'), key: 'onboarding' },
    ];

    const hit = routes.find((r) => r.match(location));
    if (!hit) return null;
    return { title: t(`nav.${hit.key}`, { defaultValue: hit.key }) };
  }, [location, t]);
}

function timeOfDayGreeting(t: (k: string, opts?: Record<string, unknown>) => string, name?: string): string {
  const hour = new Date().getHours();
  const slot = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const base = t(`greeting.${slot}`, { defaultValue: `Good ${slot}` });
  return name ? `${base}, ${name}` : base;
}

interface AdminTopBarProps {
  organizationTimezone?: string;
  onOpenSidebar: () => void;
  homeHref: string;
  userFirstName?: string;
}

function AdminTopBar({
  organizationTimezone,
  onOpenSidebar,
  homeHref,
  userFirstName,
}: AdminTopBarProps) {
  const { t } = useTranslation('common');
  const route = useRouteGreeting();
  const pageHeader = usePageHeader();
  const [location] = useLocation();
  const isDashboard = location.startsWith('/admin/dashboard') || location.startsWith('/staff/dashboard');

  const title =
    pageHeader.title ??
    (isDashboard ? timeOfDayGreeting(t, userFirstName) : route?.title);

  return (
    <TopBar
      className="shadow-sm border-b border-line"
      leading={
        <>
          <IconButton
            icon={<Menu />}
            aria-label={t('actions.openMenu', { defaultValue: 'Open menu' })}
            variant="subtle"
            size="md"
            onClick={onOpenSidebar}
            className="lg:hidden"
          />
          {title && (
            <TopBar.Greeting
              title={title}
              sub={pageHeader.sub}
            />
          )}
        </>
      }
      trailing={
        <div className="flex items-center gap-2">
          <TimezoneIndicator timezone={organizationTimezone} />
          <LanguagePill />
          <Link href="/admin/tips">
            <IconButton
              icon={<HelpCircle />}
              aria-label={t('nav.helpTips', { defaultValue: 'Help' })}
              variant="subtle"
              size="md"
            />
          </Link>
          {pageHeader.actions && <TopBar.Divider className="hidden md:inline-block" />}
          {pageHeader.actions}
        </div>
      }
    />
  );
}

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  tourId?: string;
  matchPrefix?: boolean;
  trailing?: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const { t } = useTranslation('common');
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handler = () => setSidebarOpen(true);
    window.addEventListener('onboarding-tour:open-sidebar', handler);
    return () => window.removeEventListener('onboarding-tour:open-sidebar', handler);
  }, []);
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });

  // Sloty Sessions (therapy session notes): providers in a psychotherapy practice only.
  // The padlock says what the page is: notes only their author can read.
  const showSessions = isTherapyPractice(organization?.business_type, user?.hasProviderProfile);
  const sessionsItem: NavItem = {
    name: t('nav.sessions'),
    href: '/admin/sessions',
    icon: NotebookPen,
    tourId: 'tour-sessions',
    matchPrefix: true,
    trailing: <Lock className="ml-2 h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />,
  };

  // Staff navigation - workspace items only; settings pinned to bottom.
  const staffNavigation: Record<'workspace' | 'settings', NavItem[]> = {
    workspace: [
      { name: t('nav.dashboard'), href: '/staff/dashboard', icon: BarChart3, tourId: 'tour-dashboard' },
      { name: t('nav.mySchedule'), href: '/staff/schedule', icon: Clock, tourId: 'tour-my-schedule' },
      { name: t('nav.myBookings'), href: '/staff/bookings', icon: Calendar, tourId: 'tour-my-bookings' },
      ...(showSessions ? [sessionsItem] : []),
    ],
    settings: [
      { name: t('nav.calendarIntegration'), href: '/staff/calendar-settings', icon: CalendarCheck, tourId: 'tour-calendar-integration' },
      { name: t('nav.settings'), href: '/staff/settings', icon: Settings, tourId: 'tour-settings' },
    ],
  };

  // Admin navigation - grouped: workspace, manage, settings.
  // Schedule/Bookings point to the team views, which cover the admin's own
  // schedule and bookings via the provider filter — so there are no separate
  // "My Schedule" / "My Bookings" entries.
  const adminNavigation: Record<'workspace' | 'adminOnly' | 'settings', NavItem[]> = {
    workspace: [
      { name: t('nav.dashboard'), href: '/admin/dashboard', icon: BarChart3, tourId: 'tour-dashboard' },
      { name: t('nav.schedule'), href: '/admin/availability', icon: LayoutGrid, tourId: 'tour-team-schedule' },
      { name: t('nav.bookings'), href: '/admin/bookings', icon: Calendar, tourId: 'tour-team-bookings' },
      ...(showSessions ? [sessionsItem] : []),
    ],
    adminOnly: [
      { name: t('nav.services'), href: '/admin/services', icon: Package, tourId: 'tour-services' },
      { name: t('nav.team'), href: '/admin/team', icon: Users, tourId: 'tour-team' },
      { name: t('nav.customers'), href: '/admin/customers', icon: UserSquare2, tourId: 'tour-customers' },
      {
        name: t('nav.bookingPage'),
        href: '/admin/booking-page',
        icon: Globe,
        // The public page is always live for an active org.
        trailing: (
          <span
            className="ml-auto h-2 w-2 shrink-0 rounded-pill bg-success"
            role="img"
            aria-label={t('nav.bookingPageLive', { defaultValue: 'Live' })}
            data-testid="booking-page-live-dot"
          />
        ),
      },
    ],
    settings: [
      { name: t('nav.calendarIntegration'), href: '/admin/calendar-settings', icon: CalendarCheck, tourId: 'tour-calendar-integration' },
      { name: t('nav.settings'), href: '/admin/settings', icon: Settings, tourId: 'tour-settings' },
      { name: t('nav.billing'), href: '/admin/billing', icon: CreditCard, tourId: 'tour-billing' },
      { name: t('nav.helpTips'), href: '/admin/tips', icon: Lightbulb },
    ],
  };

  const isAdmin = user?.role !== 'staff';
  const homeHref = user?.role === 'staff' ? '/staff/dashboard' : '/admin/dashboard';

  // Bottom tab bar on mobile (<lg). Four most-used routes per role; the
  // "More" slot reopens the existing drawer-sidebar for everything else.
  const mobileTabs: MobileTabItem[] = isAdmin
    ? [
      { href: '/admin/availability', icon: LayoutGrid, label: t('nav.schedule'), matchPrefix: true },
      { href: '/admin/bookings', icon: Calendar, label: t('nav.bookings'), matchPrefix: true },
      showSessions
        ? { href: '/admin/sessions', icon: NotebookPen, label: t('nav.sessions'), matchPrefix: true }
        : { href: '/admin/customers', icon: UserSquare2, label: t('nav.customers'), matchPrefix: true },
    ]
    : [
      { href: '/staff/schedule', icon: Clock, label: t('nav.mySchedule'), matchPrefix: true },
      { href: '/staff/bookings', icon: Calendar, label: t('nav.myBookings'), matchPrefix: true },
      showSessions
        ? { href: '/admin/sessions', icon: NotebookPen, label: t('nav.sessions'), matchPrefix: true }
        : { href: '/admin/customers', icon: UserSquare2, label: t('nav.customers'), matchPrefix: true },
    ];

  return (
    <PageHeaderProvider>
      <div className="min-h-screen bg-bg-app overflow-x-hidden">
        <OnboardingTour />

        <Sidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onCloseDrawer={() => setSidebarOpen(false)}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        >
          <Sidebar.Brand
            href={homeHref}
            logoSrc="/images/logo_horizontal_white.png"
            alt="Sloty"
          />
          <Sidebar.Body>
            <Sidebar.Nav>
              {!isAdmin && (
                <>
                  <Sidebar.Section label={t('nav.myWorkspace')} />
                  {staffNavigation.workspace.map((item) => (
                    <Sidebar.Item
                      key={item.name}
                      href={item.href}
                      id={item.tourId}
                      icon={item.icon}
                      label={item.name}
                      matchPrefix={item.matchPrefix}
                      trailing={item.trailing}
                    />
                  ))}
                </>
              )}

              {isAdmin && (
                <>
                  <Sidebar.Section label={t('nav.workspace')} />
                  {adminNavigation.workspace.map((item) => (
                    <Sidebar.Item
                      key={item.name}
                      href={item.href}
                      id={item.tourId}
                      icon={item.icon}
                      label={item.name}
                      matchPrefix={item.matchPrefix}
                      trailing={item.trailing}
                    />
                  ))}
                  <Sidebar.Section label={t('nav.manage')} />
                  {adminNavigation.adminOnly.map((item) => (
                    <Sidebar.Item
                      key={item.name}
                      href={item.href}
                      id={item.tourId}
                      icon={item.icon}
                      label={item.name}
                      matchPrefix={item.matchPrefix}
                      trailing={item.trailing}
                    />
                  ))}
                </>
              )}
            </Sidebar.Nav>
            <div className="shrink-0 space-y-1 px-2 pb-3">
              <Sidebar.Section label={t('nav.account')} />
              {(isAdmin ? adminNavigation.settings : staffNavigation.settings).map((item) => (
                <Sidebar.Item
                  key={item.name}
                  href={item.href}
                  id={item.tourId}
                  icon={item.icon}
                  label={item.name}
                />
              ))}
            </div>
          </Sidebar.Body>
          <Sidebar.Foot
            profileHref={isAdmin ? '/admin/settings?tab=myProfile' : '/staff/settings?tab=myProfile'}
            firstName={user?.firstName}
            lastName={user?.lastName}
            role={user?.role}
            onLogout={logout}
            logoutLabel={t('actions.logout')}
          />
        </Sidebar>

        {/* Main content */}
        <div className={cn('transition-all duration-300', sidebarCollapsed ? 'lg:pl-14' : 'lg:pl-60')}>
          <AdminTopBar
            organizationTimezone={organization?.timezone}
            onOpenSidebar={() => setSidebarOpen(true)}
            homeHref={homeHref}
            userFirstName={user?.firstName}
          />

          {/* Page content */}
          <main className="p-4 sm:p-6 lg:p-8 pb-32 sm:pb-8">
            {/* Email Confirmation Banner */}
            <div className="mb-6">
              <EmailConfirmationBanner />
            </div>

            {children}
          </main>
        </div>

        <MobileTabBar
          items={[
            ...mobileTabs,
            {
              icon: Menu,
              label: t('actions.more', { defaultValue: 'More' }),
              onClick: () => setSidebarOpen(true),
            },
          ]}
        />
      </div>
    </PageHeaderProvider>
  );
}
